// client/src/components/stats/FoldToPushBreakdown.tsx
//
// Muestra el breakdown de Fold to Push por stack bin del shover.
// Tabla simple: ≤10bb / 10-20bb / 20-30bb / >30bb con benchmark Nash.

import React from 'react';
import { FoldToPushDerived } from '../../utils/pokerStats/types';
import { MIN_FOLD_TO_PUSH_SAMPLE } from '../../utils/pokerStats/constants';
import { useT } from '../../lib/i18n';
import { useBenchmarks } from '../../contexts/BenchmarkContext';

interface FoldToPushBreakdownProps {
  data: FoldToPushDerived[];
  label?: string;
}

export function FoldToPushBreakdown({ data, label }: FoldToPushBreakdownProps) {
  const { t } = useT();
  const bm = useBenchmarks();

  const totalOpp = data.reduce((s, d) => s + d.opp, 0);

  if (totalOpp === 0) {
    return (
      <div className="rfi-breakdown rfi-empty">
        <p className="rfi-empty-text">{t('stats.foldToPush.empty')}</p>
      </div>
    );
  }

  const anySufficient = data.some(d => !d.lowSample && d.opp > 0);

  return (
    <div className="rfi-breakdown">
      {label && <div className="rfi-breakdown-title">{label}</div>}

      {!anySufficient && (
        <div className="rfi-warning">{t('stats.foldToPush.warning', { n: MIN_FOLD_TO_PUSH_SAMPLE })}</div>
      )}

      <table className="rfi-table">
        <thead>
          <tr>
            <th>{t('stats.foldToPush.thStack')}</th>
            <th className="rfi-th-num">{t('stats.foldToPush.thFold')}</th>
            <th className="rfi-th-num">{t('stats.foldToPush.thBenchmark')}</th>
            <th className="rfi-th-num">{t('stats.foldToPush.thSample')}</th>
            <th className="rfi-th-status" />
          </tr>
        </thead>
        <tbody>
          {data.map(d => {
            const benchmark = bm.getFoldToPushBenchmark(d.bin);
            const status = d.lowSample || d.opp === 0 ? 'ok' : bm.evalFoldToPush(d.bin, d.foldPct);
            const showEval = !d.lowSample && d.opp > 0 && benchmark;

            return (
              <tr
                key={d.bin}
                className={`rfi-row rfi-row-${status} ${d.lowSample ? 'rfi-row-low' : ''}`}
              >
                <td className="rfi-cell-pos">{d.label}</td>
                <td className="rfi-cell-num rfi-cell-value">
                  {d.opp > 0 ? `${d.foldPct}%` : '—'}
                </td>
                <td className="rfi-cell-num rfi-cell-benchmark">
                  {benchmark ? `${benchmark.min}–${benchmark.max}%` : '—'}
                </td>
                <td className="rfi-cell-num rfi-cell-sample">
                  {d.opp > 0 ? `n=${d.opp}` : '—'}
                  {d.lowSample && d.opp > 0 && (
                    <span className="rfi-low-tag"> {t('stats.foldToPush.lowTag')}</span>
                  )}
                </td>
                <td className="rfi-cell-status">
                  {showEval && <span className={`stat-dot dot-${status}`} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
